// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Coordinates authorized workspace creation from an application and template.
 *
 * The wizard filters choices by the actor's creation rights, gathers the
 * application-specific request prefix, and submits one canonical API mutation.
 * Step components remain presentation-only so they cannot bypass validation.
 */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppWindow, ChevronLeft, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { AppConfigProvider } from '../../context/AppConfigContext';
import { useAuth } from '../../context/AuthContext';
import {
  applicationKeys,
  useWorkspaceWizardApi,
  type ApplicationInfo,
  type WorkspaceWizardApi,
  type TemplateInfo,
} from '../../services/templates-api';
import {
  ApplicationStep,
  NameStep,
  TemplateStep,
  WizardStepper,
  type TemplateChoice,
  type WizardStep,
} from './WorkspaceWizardSteps';

const APPS_REQUIRING_REQUEST_PREFIX = ['contract-review', 'srm'];

export interface WorkspaceWizardProps {
  app_id: string;
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkspaceCreated?: (orgId: string, wsId: string) => void;
  debug?: boolean;
  api?: WorkspaceWizardApi;
}

type WorkspaceWizardInternalProps = Omit<WorkspaceWizardProps, 'app_id' | 'debug'>;

function WorkspaceWizardInternal({
  orgId,
  open,
  onOpenChange,
  onWorkspaceCreated,
  api: injectedApi,
}: WorkspaceWizardInternalProps) {
  const defaultApi = useWorkspaceWizardApi();
  const appApi = injectedApi ?? defaultApi;
  const queryClient = useQueryClient();
  const { isGlobalAdmin, creatableAppIds, currentOrgRoles } = useAuth();
  const [selectedApp, setSelectedApp] = useState<ApplicationInfo | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateChoice>(null);
  const [workspaceName, setWorkspaceName] = useState('');
  const [requestPrefix, setRequestPrefix] = useState('');
  const [step, setStep] = useState<WizardStep>('application');

  const requiresPrefix = APPS_REQUIRING_REQUEST_PREFIX.includes(selectedApp?.appId ?? '');
  const prefixValid = /^[A-Z0-9]{1,6}$/.test(requestPrefix);
  const canCreate = Boolean(workspaceName.trim()) && (!requiresPrefix || prefixValid);

  const { data: allApplications = [], isLoading } = useQuery({
    queryKey: applicationKeys.list(),
    queryFn: () => appApi.listApplications(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const { data: templates = [], isLoading: templatesLoading, isSuccess: templatesLoaded } = useQuery({
    queryKey: applicationKeys.templates(selectedApp?.appId ?? ''),
    queryFn: () => appApi.listTemplates(selectedApp!.appId),
    enabled: open && Boolean(selectedApp),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (step === 'template' && templatesLoaded && templates.length === 0) {
      setSelectedTemplate('blank');
      setStep('name');
    }
  }, [step, templatesLoaded, templates.length]);

  const isOrgAdmin = currentOrgRoles.includes('admin');
  const applications = useMemo(() => {
    if (isGlobalAdmin || isOrgAdmin) return allApplications;
    return allApplications.filter(app => creatableAppIds.includes(app.appId));
  }, [allApplications, isGlobalAdmin, isOrgAdmin, creatableAppIds]);

  const resetState = () => {
    setSelectedApp(null);
    setSelectedTemplate(null);
    setWorkspaceName('');
    setRequestPrefix('');
    setStep('application');
  };

  const handleClose = () => {
    onOpenChange(false);
    resetState();
  };

  const createMutation = useMutation({
    mutationFn: () => {
      if (!selectedApp || !selectedTemplate) throw new Error('Workspace selection is incomplete');
      return appApi.createFromApplication(orgId, selectedApp.appId, {
        name: workspaceName.trim(),
        templateId: selectedTemplate === 'blank' ? undefined : selectedTemplate.templateId,
        requestPrefix: requiresPrefix ? requestPrefix : undefined,
      });
    },
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success(`Workspace "${workspaceName.trim()}" created`);
      handleClose();
      onWorkspaceCreated?.(result.orgId, result.wsId);
    },
    onError: error => {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error('Failed to create workspace', {
        description: detail ?? (error instanceof Error ? error.message : undefined),
      });
    },
  });

  const handleSelectApplication = (app: ApplicationInfo) => {
    if (selectedApp?.appId !== app.appId) setSelectedTemplate(null);
    setSelectedApp(app);
    setStep('template');
  };

  const handleSelectTemplate = (template: TemplateInfo | 'blank') => {
    setSelectedTemplate(template);
    setStep('name');
  };

  const handleBack = () => {
    if (step === 'name' && templates.length > 0) setStep('template');
    else setStep('application');
  };

  const handleCreate = () => {
    if (canCreate && !createMutation.isPending) createMutation.mutate();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleCreate();
  };

  const title = step === 'application'
    ? 'Create workspace'
    : step === 'template'
      ? 'Choose a template'
      : 'Name your workspace';
  const description = step === 'application'
    ? 'Choose an application for your new workspace'
    : step === 'template'
      ? `Start from a "${selectedApp?.name}" template or a blank workspace`
      : `Creating workspace from template "${selectedTemplate === 'blank' ? 'Blank Workspace' : selectedTemplate?.name}"`;

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && handleClose()}>
      <DialogContent
        data-testid="workspace-wizard"
        showCloseButton={false}
        className="max-h-[88vh] gap-0 overflow-hidden rounded-[14px] border-0 p-0 shadow-[0_24px_64px_rgba(15,23,42,0.28)] sm:max-w-[640px]"
      >
        <DialogHeader className="flex-row items-start justify-between gap-4 px-6 pb-0 pt-6 text-left">
          <div className="flex min-w-0 items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[#eef2f7] text-[#475569]">
              <AppWindow className="h-4.5 w-4.5" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-[650] leading-[1.3] tracking-[-0.01em] text-[#0f172a]">{title}</DialogTitle>
              <DialogDescription className="mt-1 text-[13.5px] leading-[1.5] text-[#64748b]">{description}</DialogDescription>
            </div>
          </div>
          <DialogClose asChild>
            <button type="button" className="-mr-1.5 -mt-1 inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] text-[#94a3b8] transition-colors duration-[120ms] ease-[ease] hover:bg-[#f1f5f9] hover:text-[#0f172a]">
              <X className="h-[17px] w-[17px]" />
              <span className="sr-only">Close</span>
            </button>
          </DialogClose>
        </DialogHeader>

        <WizardStepper step={step} onJump={setStep} />

        <div className="max-h-[60vh] overflow-y-auto px-6 pb-6 pt-4">
          <div key={step} className="docreview-wizard-step">
            {step === 'application' && (
              <ApplicationStep
                applications={applications}
                selectedApp={selectedApp}
                loading={isLoading}
                onSelect={handleSelectApplication}
              />
            )}
            {step === 'template' && (
              <TemplateStep
                templates={templates}
                selectedTemplate={selectedTemplate}
                loading={templatesLoading}
                onSelect={handleSelectTemplate}
              />
            )}
            {step === 'name' && selectedApp && selectedTemplate && (
              <NameStep
                workspaceName={workspaceName}
                requestPrefix={requestPrefix}
                requiresPrefix={requiresPrefix}
                selectedApp={selectedApp}
                selectedTemplate={selectedTemplate}
                onWorkspaceName={setWorkspaceName}
                onRequestPrefix={setRequestPrefix}
                onKeyDown={handleKeyDown}
              />
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-[#eef2f6] bg-[#fbfcfd] px-6 py-3.5 sm:justify-between">
          {step === 'application' ? (
            <Button variant="outline" onClick={handleClose} className="h-[38px] rounded-lg border-[#dde3ec] px-4 text-[13.5px] font-[550] text-[#334155] shadow-[0_1px_2px_rgba(2,8,23,0.04)] hover:bg-[#f8fafc]">Cancel</Button>
          ) : (
            <Button variant="outline" onClick={handleBack} className="h-[38px] gap-1.5 rounded-lg border-[#dde3ec] px-3.5 text-[13.5px] font-[550] text-[#334155] shadow-[0_1px_2px_rgba(2,8,23,0.04)] hover:bg-[#f8fafc]">
              <ChevronLeft className="h-[15px] w-[15px]" />
              Back
            </Button>
          )}
          {step === 'name' && (
            <Button onClick={handleCreate} disabled={!canCreate || createMutation.isPending} className="h-[38px] gap-2 rounded-lg bg-[#0f172a] px-[18px] text-[13.5px] font-[550] shadow-[0_1px_2px_rgba(2,8,23,0.18)] hover:bg-[#1e293b] disabled:opacity-[0.45]">
              <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
              {createMutation.isPending ? 'Creating...' : 'Create workspace'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WorkspaceWizard({
  app_id,
  debug = false,
  ...props
}: WorkspaceWizardProps) {
  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <WorkspaceWizardInternal {...props} />
    </AppConfigProvider>
  );
}
